import React, { useEffect, useState } from 'react';
import { createTheme } from '@mui/material/styles';
import { AppMode } from '../../enums/AppMode';
import { globalThemeOptions } from '../../theme/GlobalTheme';
import { PERFORMANCE_MEASURE_NAMES } from '../../enums/DataDog/PerformanceMeasures';
import { SignOutMessage } from '../../enums/Auth';
import Analytics from '../../tools/Analytics';
import ApolloService from '../../Services/ApolloService';
import App from './App';
import AuthTokenProvider from '../../Services/Auth/AuthTokenProvider';
import GlobalConfigurationContextProvider from '../../lib/context/GlobalConfigurationContext';
import LocalStorageStrategy from '../../Services/Auth/Strategy/LocalStorage';
import SetupZipy from '../../components/Zipy/SetupZipy';
import withCustomTiming from '../../lib/hoc/DataDog/withCustomTiming';
import withDataDog from '../../lib/hoc/DataDog/withDataDog';
import withTheme from '../../lib/hoc/withTheme';
import './fonts.css';
import loadcss from './loadcss';
import {
  UI_ASSETS_CDN_FONTS_STENCIL_URL,
  UI_ASSETS_CDN_STYLES_STENCIL_URL,
  UI_ASSETS_CDN_FONTAWESOME_STENCIL_URL,
} from '../../config/env';

loadcss(UI_ASSETS_CDN_FONTS_STENCIL_URL);
loadcss(UI_ASSETS_CDN_STYLES_STENCIL_URL);
loadcss(UI_ASSETS_CDN_FONTAWESOME_STENCIL_URL);

/**
 * Starting standalone tools
 */
Analytics.register();

/**
 * Preparing the Apollo client to handle unauthorized requests to sign out
 * the user, in order to redirect them to the login page when the session
 * has expired.
 */
const unauthorizedHandler = async () =>
  AuthTokenProvider.signOut({
    signOutMessage: SignOutMessage.SessionExpired,
  });

const globalTheme = createTheme(globalThemeOptions);
const StyledApp = withTheme(App, globalTheme);

const StandaloneWrapper: React.FC = (): JSX.Element => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    ApolloService.setUnauthorizedRequestHandler(unauthorizedHandler);

    AuthTokenProvider.setStrategy(new LocalStorageStrategy());
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GlobalConfigurationContextProvider
      allowStandaloneCustomization
      allowStandaloneRouting
      appMode={AppMode.Standalone}
    >
      <SetupZipy />
      {ready && <StyledApp />}
    </GlobalConfigurationContextProvider>
  );
};

export default withDataDog(
  withCustomTiming(
    StandaloneWrapper,
    PERFORMANCE_MEASURE_NAMES.DSR_RENDERING_STARTED
  )
);
